<template>
  <section class="section">
    <section class="container is-max-widescreen">
      <h1 class="title has-text-centered">Migration de tâches entre salariés</h1>

      <!-- Bandeau explicatif -->
      <div class="box">
        <h2 class="subtitle">Avant de commencer</h2>
        <p class="mb-4">
          Cet outil permet de transférer les tâches d'un salarié vers un autre, en conservant l'historique.
          Les tâches de l'ancien salarié seront archivées (jamais supprimées) et de nouvelles tâches
          seront créées pour le nouveau salarié.
        </p>

        <div class="columns">
          <div class="column">
            <article class="message migration-replace">
              <div class="message-header">
                <p>Mode Remplacement (1:1)</p>
              </div>
              <div class="message-body has-text-dark">
                <p><strong>Cas d'usage :</strong> un intitulé de poste provisoire (ex&nbsp;: «&nbsp;Stagiaire Gironde&nbsp;»,
                  «&nbsp;Chargé(e) d'étude&nbsp;») est remplacé par le nom du salarié recruté.</p>
                <p class="mt-2"><strong>Ce qui se passe :</strong> les tâches de l'ancien sont archivées, des
                  tâches identiques sont créées pour le nouveau avec <strong>le même nombre de jours,
                  les mêmes dates</strong>.</p>
                <p class="mt-2"><strong>Exemple :</strong> le projet avait "Chargé(e) d'étude" comme nom générique
                  avec 20 jours. On recrute Marie DUPONT &rarr; elle récupère les 20 jours tels quels.</p>
              </div>
            </article>
          </div>
          <div class="column">
            <article class="message migration-prorata">
              <div class="message-header">
                <p>Mode Prorata (calcul du temps restant)</p>
              </div>
              <div class="message-body">
                <p><strong>Cas d'usage :</strong> un salarié part en cours d'année et quelqu'un d'autre reprend
                  ses projets. Le partant a déjà travaillé une partie du temps.</p>
                <p class="mt-2"><strong>Ce qui se passe :</strong> on calcule combien de jours restent à faire
                  (soit par ratio temporel, soit en indiquant manuellement les jours déjà consommés), et la
                  nouvelle tâche est créée avec <strong>uniquement les jours restants</strong> et une
                  <strong>date de début = date de bascule</strong>.</p>
                <p class="mt-2"><strong>Exemple 1 :</strong> Le salarié A avait 20 jours du 01/01 au 31/12/2026.
                  Il part le 01/07. Temps écoulé = 50% &rarr; le salarié B récupère
                  <strong>10 jours</strong>, du 01/07 au 31/12.</p>
                <p class="mt-2"><strong>Exemple 2 (pluriannuel) :</strong>
                  B arrive le 03/11/2025 (date de bascule) et reprend des projets de A commencés en 2024.</p>
                <table class="table is-narrow is-bordered is-size-7 mt-4 mb-1">
                  <thead><tr><th>Année</th><th>Salarié A</th><th>Salarié B</th></tr></thead>
                  <tbody>
                    <tr><td>2024</td><td>Tâches archivées (historique intact)</td><td>Rien créé (pas encore en poste)</td></tr>
                    <tr><td>2025</td><td>Archivé (janv. &rarr; oct.)</td><td>Reprend au prorata (nov. &rarr; déc.)</td></tr>
                    <tr><td>2026</td><td>Archivé</td><td>Reprend l'intégralité des jours</td></tr>
                  </tbody>
                </table>
              </div>
            </article>
          </div>
        </div>
      </div>

      <hr>

      <!-- Formulaire -->
      <div class="box">
        <h2 class="subtitle">Paramètres de la migration</h2>

        <div class="columns">
          <div class="column is-half">
            <b-field label="Ancien salarié (source)">
              <b-autocomplete
                  v-model="fromInput"
                  :data="filteredFromList"
                  placeholder="Nom du salarié à remplacer"
                  icon="user-minus"
                  field="name"
                  clearable
                  @select="option => fromSelected = option">
                <template #empty>Pas de résultats</template>
                <template slot-scope="props">
                  <div>
                    {{ props.option.name }}
                    <small class="has-text-grey"> - {{ props.option.service }}</small>
                  </div>
                </template>
              </b-autocomplete>
            </b-field>
          </div>

          <div class="column is-half">
            <b-field label="Nouveau salarié (cible)">
              <b-autocomplete
                  v-model="toInput"
                  :data="filteredToList"
                  placeholder="Nom du salarié qui reprend"
                  icon="user-plus"
                  field="name"
                  clearable
                  @select="option => toSelected = option">
                <template #empty>Pas de résultats</template>
                <template slot-scope="props">
                  <div>
                    {{ props.option.name }}
                    <small class="has-text-grey"> - {{ props.option.service }}</small>
                  </div>
                </template>
              </b-autocomplete>
            </b-field>
          </div>
        </div>

        <div class="columns">
          <div class="column is-one-third">
            <b-field label="Mode de migration">
              <b-select v-model="mode" expanded>
                <option value="replace">Remplacement (1:1)</option>
                <option value="prorata">Prorata (temps restant)</option>
              </b-select>
            </b-field>
          </div>

          <div class="column is-one-third" v-if="mode === 'prorata'">
            <b-field label="Date de bascule" grouped>
              <b-select v-model="switchDay" placeholder="Jour">
                <option v-for="d in 31" :key="d" :value="d">{{ d }}</option>
              </b-select>
              <b-select v-model="switchMonth" placeholder="Mois" expanded>
                <option v-for="(m, i) in months" :key="i" :value="i + 1">{{ m }}</option>
              </b-select>
              <b-select v-model="switchYear" placeholder="Année">
                <option v-for="y in yearRange" :key="y" :value="y">{{ y }}</option>
              </b-select>
            </b-field>
          </div>

          <div class="column is-one-third" v-if="mode === 'prorata'">
            <b-field label="Jours déjà consommés (optionnel)">
              <b-input
                  v-model.number="joursConsommes"
                  type="number"
                  step="0.5"
                  min="0"
                  placeholder="Laisser vide pour calcul auto">
              </b-input>
            </b-field>
          </div>
        </div>

        <div class="columns">
          <div class="column is-one-third">
            <b-field label="Filtrer par projet (optionnel)">
              <b-input
                  v-model="projectFilter"
                  placeholder="ex: 64DOMOND_26"
                  icon="filter">
              </b-input>
            </b-field>
          </div>
        </div>

        <div class="buttons">
          <b-button
              class="btn-simulate"
              icon-left="eye"
              :loading="isLoading"
              :disabled="!canSubmit"
              @click="preview">
            Simuler la migration
          </b-button>
        </div>

        <!-- Indicateur de scroll vers les résultats -->
        <div v-if="result" class="has-text-centered scroll-hint" @click="scrollToResult">
          <b-icon icon="arrow-down" size="is-medium"></b-icon>
          <p>Voir le résultat ci-dessous</p>
        </div>
      </div>

      <!-- Résultats de la simulation -->
      <div class="box" v-if="result" ref="resultBox">
        <div class="level mb-4">
          <div class="level-left">
            <h2 class="subtitle mb-0">
              <b-icon icon="clipboard-check" size="is-small"></b-icon>
              Rapport {{ result.dryRun ? 'de simulation' : 'd\'exécution' }}
            </h2>
          </div>
          <div class="level-right">
            <b-button
                class="btn-export"
                icon-left="download"
                @click="exportCSV">
              Exporter le rapport (CSV)
            </b-button>
          </div>
        </div>

        <!-- Phrase récap -->
        <div class="notification is-light mb-4">
          <p class="has-text-centered is-size-5">
            <strong>{{ result.from }}</strong> &rarr; <strong>{{ result.to }}</strong>
            &mdash; mode <strong>{{ mode === 'replace' ? 'remplacement' : 'prorata' }}</strong>
          </p>
          <p class="has-text-centered" v-if="result.tasksArchived > 0">
            {{ result.tasksArchived }} tâche(s) archivée(s),
            {{ result.tasksCreated }} tâche(s) créée(s) pour {{ result.to }}
          </p>
        </div>

        <!-- Compteurs -->
        <div class="columns">
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Documents modifiés</p>
              <p class="title">{{ result.docsModified }}</p>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Tâches archivées</p>
              <p class="title">{{ result.tasksArchived }}</p>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Tâches créées</p>
              <p class="title">{{ result.tasksCreated }}</p>
            </div>
          </div>
          <div class="column is-one-quarter">
            <div class="notification is-light">
              <p class="heading">Répartition des marges</p>
              <p class="title is-6">{{ ventilationLabel }}</p>
              <p class="is-size-7 has-text-grey mt-1" v-if="result.ventilationStatus === 'created'">
                Une ligne sera créée pour {{ result.to }} dans la ventilation des marges
              </p>
              <p class="is-size-7 has-text-grey mt-1" v-else-if="result.ventilationStatus === 'exists'">
                {{ result.to }} a déjà une ligne dans la ventilation des marges
              </p>
              <p class="is-size-7 has-text-grey mt-1" v-else-if="result.ventilationStatus === 'skip-stagiaire'">
                Les stagiaires n'ont pas de ventilation des marges
              </p>
            </div>
          </div>
        </div>

        <!-- Statut référentiel salarié par année -->
        <div class="notification is-info is-light mb-4" v-if="result.byYear && result.byYear.some(y => y.salariesStatus === 'added')">
          <p>
            <b-icon icon="user-plus" size="is-small"></b-icon>
            <strong>{{ result.to }}</strong> — ajout au référentiel :
          </p>
          <div class="mt-2">
            <span v-for="ys in result.byYear" :key="ys.year" class="tag is-medium mr-2 mb-1"
                  :class="salariesTagClass(ys)">
              <strong class="mr-1">{{ ys.year }}</strong> :
              <span v-if="ys.salariesStatus === 'added'" class="ml-1">absent → sera ajouté (salaries + marges)</span>
              <span v-else-if="ys.salariesStatus === 'unchanged' && ys.tasksCreated > 0" class="ml-1">déjà présent</span>
              <span v-else class="ml-1">aucune tâche migrée</span>
            </span>
          </div>
        </div>

        <!-- Résumé par année -->
        <div class="mb-4" v-if="yearSummary.length > 0">
          <h3 class="subtitle is-6 mb-2">Résumé par année</h3>
          <div class="tags">
            <span class="tag is-medium" v-for="ys in yearSummary" :key="ys.year">
              <strong class="mr-1">{{ ys.year }}</strong> :
              <span v-if="ys.transferred > 0" class="ml-1">{{ ys.transferred }} transférée(s)</span>
              <span v-if="ys.transferred > 0 && ys.skipped > 0">,</span>
              <span v-if="ys.skipped > 0" class="ml-1">{{ ys.skipped }} ignorée(s)</span>
            </span>
          </div>
        </div>

        <!-- Légende des couleurs -->
        <div class="legend mb-4">
          <span class="legend-item">
            <span class="tag tag-prorata is-small">Prorata</span>
            <small>Jours restants calculés et transférés</small>
          </span>
          <span class="legend-item">
            <span class="tag tag-integral is-small">Transfert intégral</span>
            <small>100% des jours transférés (tâche après la bascule)</small>
          </span>
          <span class="legend-item" v-if="mode === 'replace'">
            <span class="tag is-info is-small">Remplacé</span>
            <small>Tâche reprise à l'identique</small>
          </span>
          <span class="legend-item">
            <span class="tag tag-skip is-small">Ignoré</span>
            <small>Tâche terminée avant la bascule, non touchée</small>
          </span>
          <span class="legend-item">
            <span class="tag is-warning is-small">Report</span>
            <small>Projet de report (REP)</small>
          </span>
          <span class="legend-item">
            <span class="tag is-light is-small">Non validé</span>
            <small>Projet non encore validé</small>
          </span>
        </div>

        <!-- Tableau de détail -->
        <b-table
            v-if="result.details && result.details.length > 0"
            :data="result.details"
            :paginated="result.details.length > 20"
            :per-page="30"
            striped
            narrowed
            default-sort="year">
          <b-table-column field="year" label="Année" centered sortable v-slot="props">
            <b-tag>{{ props.row.year }}</b-tag>
          </b-table-column>
          <b-table-column field="docId" label="Projet" sortable v-slot="props">
            {{ props.row.docId }}
            <b-tag v-if="props.row.isReport" type="is-warning" size="is-small" class="ml-1">Report</b-tag>
            <b-tag v-if="!props.row.isValidated" type="is-light" size="is-small" class="ml-1">Non validé</b-tag>
          </b-table-column>
          <b-table-column field="salarie" label="Tâche" v-slot="props">
            {{ props.row.salarie }}
          </b-table-column>
          <b-table-column field="oldDays" label="Jours initiaux" centered v-slot="props">
            {{ props.row.oldDays }}
          </b-table-column>
          <b-table-column field="consumedDays" label="Jours consommés (A)" centered v-slot="props"
                          v-if="mode === 'prorata'">
            <span v-if="props.row.consumedDays !== undefined">{{ props.row.consumedDays }}</span>
            <span v-else-if="props.row.status === 'skip'" class="has-text-grey is-size-7">Terminée</span>
            <span v-else class="has-text-grey is-size-7">0 (intégral)</span>
          </b-table-column>
          <b-table-column field="newDays" label="Jours repris (B)" centered v-slot="props">
            <span :class="{'has-text-grey': props.row.status === 'skip'}">
              {{ props.row.newDays !== null ? props.row.newDays : '—' }}
            </span>
          </b-table-column>
          <b-table-column field="action" label="Action" centered v-slot="props">
            <span class="tag" :class="actionTagClass(props.row)">
              {{ actionLabel(props.row) }}
            </span>
          </b-table-column>
        </b-table>

        <!-- Avertissement mode replace si temps assigné -->
        <div v-if="result.warning" class="notification is-warning mt-4">
          <p><strong>{{ result.warning }}</strong></p>
        </div>

        <!-- Bouton d'exécution -->
        <div class="buttons mt-4" v-if="result.dryRun && result.tasksCreated > 0">
          <b-button
              type="is-danger"
              icon-left="check-circle"
              :loading="isExecuting"
              @click="confirmExecute">
            Exécuter la migration
          </b-button>
          <p class="help is-danger">
            Cette opération va archiver {{ result.tasksArchived }} tâche(s)
            et en créer {{ result.tasksCreated }} nouvelle(s). Cette action est irréversible.
          </p>
        </div>

        <!-- Message si aucune tâche -->
        <div v-if="result.tasksCreated === 0 && result.tasksArchived === 0" class="notification is-warning">
          <span v-if="projectFilter">
            Le projet "{{ projectFilter }}" n'a pas de tâche assignée à "{{ result.from }}".
          </span>
          <span v-else>
            Aucune tâche trouvée pour "{{ result.from }}". Vérifiez le nom exact du salarié.
          </span>
        </div>

        <!-- Confirmation exécution réussie -->
        <div v-if="!result.dryRun && result.success" class="notification is-success mt-4">
          Migration terminée avec succès. {{ result.tasksArchived }} tâche(s) archivée(s),
          {{ result.tasksCreated }} tâche(s) créée(s) pour {{ result.to }}.
        </div>
      </div>

    </section>
  </section>
</template>

<script>
export default {
  name: "MigrationSalaries",
  mounted() {
    this.loadSalaries();
  },
  computed: {
    filteredFromList() {
      return this.salariesList.filter(s =>
          s.name.toLowerCase().indexOf(this.fromInput.toLowerCase()) >= 0
      );
    },
    filteredToList() {
      return this.salariesList.filter(s =>
          s.name.toLowerCase().indexOf(this.toInput.toLowerCase()) >= 0
      );
    },
    canSubmit() {
      if (!this.fromSelected || !this.toSelected) return false;
      if (this.fromSelected.name === this.toSelected.name) return false;
      if (this.mode === 'prorata' && !this.switchDateStr && !this.joursConsommes) return false;
      return true;
    },
    switchDateStr() {
      if (!this.switchDay || !this.switchMonth || !this.switchYear) return '';
      const m = String(this.switchMonth).padStart(2, '0');
      const d = String(this.switchDay).padStart(2, '0');
      return this.switchYear + '-' + m + '-' + d;
    },
    ventilationLabel() {
      if (!this.result) return '';
      switch (this.result.ventilationStatus) {
        case 'created': return 'Créée';
        case 'exists': return 'Déjà présente';
        case 'skip-stagiaire': return 'Non nécessaire';
        case 'unchanged': return 'Inchangée';
        default: return this.result.ventilationStatus;
      }
    },
    months() {
      return ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
              'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
    },
    yearRange() {
      const current = new Date().getFullYear();
      const years = [];
      for (let y = current - 3; y <= current + 2; y++) years.push(y);
      return years;
    },
    yearSummary() {
      if (!this.result || !this.result.details) return [];
      const map = {};
      this.result.details.forEach(d => {
        const y = d.year || '?';
        if (!map[y]) map[y] = { year: y, transferred: 0, skipped: 0 };
        if (d.status === 'skip') {
          map[y].skipped++;
        } else {
          map[y].transferred++;
        }
      });
      return Object.values(map).sort((a, b) => a.year - b.year);
    }
  },
  methods: {
    loadSalaries() {
      this.$axios.get('migration/salaries')
          .then(res => {
            this.salariesList = res.data.salaries;
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur de chargement des salariés : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          });
    },
    actionLabel(row) {
      if (row.status === 'skip') return 'Ignoré (terminée)';
      switch (row.action) {
        case 'replace': return 'Remplacé';
        case 'prorata': return 'Prorata';
        case 'prorata-full': return 'Transfert intégral';
        default: return row.status;
      }
    },
    actionTagClass(row) {
      if (row.status === 'skip') return 'tag-skip';
      switch (row.action) {
        case 'replace': return 'is-info';
        case 'prorata': return 'tag-prorata';
        case 'prorata-full': return 'tag-integral';
        default: return 'is-light';
      }
    },
    salariesTagClass(ys) {
      if (ys.salariesStatus === 'added') return 'is-success is-light';
      if (ys.salariesStatus === 'unchanged' && ys.tasksCreated > 0) return 'is-info is-light';
      return 'is-light';
    },
    scrollToResult() {
      this.$nextTick(() => {
        if (this.$refs.resultBox) {
          this.$refs.resultBox.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    exportCSV() {
      if (!this.result || !this.result.details) return;
      const sep = ';';
      const headers = ['Année', 'Projet', 'Tâche', 'Jours initiaux'];
      if (this.mode === 'prorata') headers.push('Jours consommés');
      headers.push('Jours repris', 'Action', 'Statut');

      const rows = this.result.details.map(d => {
        const row = [
          d.year || '',
          d.docId || '',
          d.salarie || '',
          d.oldDays !== undefined ? d.oldDays : ''
        ];
        if (this.mode === 'prorata') row.push(d.consumedDays !== undefined ? d.consumedDays : '');
        row.push(
          d.newDays !== null && d.newDays !== undefined ? d.newDays : '',
          this.actionLabel(d),
          d.status || ''
        );
        return row;
      });

      // Ligne résumé
      const summary = [
        '',
        'TOTAL',
        '',
        '',
      ];
      if (this.mode === 'prorata') summary.push('');
      summary.push('', 'Archivées: ' + this.result.tasksArchived + ' / Créées: ' + this.result.tasksCreated, '');

      let csv = '\uFEFF'; // BOM UTF-8 pour Excel
      csv += 'Rapport de migration - ' + this.result.from + ' vers ' + this.result.to + '\n';
      csv += 'Mode: ' + (this.mode === 'replace' ? 'Remplacement' : 'Prorata') + '\n';
      if (this.switchDateStr) csv += 'Date de bascule: ' + this.switchDateStr + '\n';
      csv += 'Date du rapport: ' + new Date().toLocaleDateString('fr-FR') + '\n';
      csv += '\n';
      csv += headers.join(sep) + '\n';
      rows.forEach(r => { csv += r.join(sep) + '\n'; });
      csv += summary.join(sep) + '\n';

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'migration_' + this.result.from.replace(/\s+/g, '_') + '_vers_' + this.result.to.replace(/\s+/g, '_') + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    },
    preview() {
      this.isLoading = true;
      this.result = null;

      const params = {
        from: this.fromSelected.name,
        to: this.toSelected.name,
        mode: this.mode,
        date: this.switchDateStr || null,
        joursConsommes: this.joursConsommes || null,
        project: this.projectFilter || null
      };

      this.$axios.post('migration/preview', params)
          .then(res => {
            this.result = res.data;
            this.scrollToResult();
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          })
          .finally(() => {
            this.isLoading = false;
          });
    },
    confirmExecute() {
      const warningHtml = this.result.warning
          ? `<div class="notification is-warning"><p>${this.result.warning}</p></div>`
          : '';
      this.$buefy.dialog.confirm({
        title: 'Confirmer la migration',
        message: `${warningHtml}
          <p>Vous êtes sur le point de :</p>
          <ul>
            <li>Archiver <strong>${this.result.tasksArchived}</strong> tâche(s) de <strong>${this.result.from}</strong></li>
            <li>Créer <strong>${this.result.tasksCreated}</strong> tâche(s) pour <strong>${this.result.to}</strong></li>
          </ul>
          <p class="mt-2"><strong>Cette action est irréversible.</strong></p>`,
        confirmText: this.result.warning ? 'Je confirme malgré l\'avertissement' : 'Oui, exécuter',
        cancelText: 'Annuler',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.execute()
      });
    },
    execute() {
      this.isExecuting = true;

      const params = {
        from: this.fromSelected.name,
        to: this.toSelected.name,
        mode: this.mode,
        date: this.switchDateStr || null,
        joursConsommes: this.joursConsommes || null,
        project: this.projectFilter || null
      };

      this.$axios.post('migration/execute', params)
          .then(res => {
            this.result = res.data;
            this.$buefy.toast.open({
              message: 'Migration effectuée avec succès !',
              type: 'is-success'
            });
          })
          .catch(err => {
            this.$buefy.toast.open({
              message: 'Erreur : ' + (err.response ? err.response.data.error : err.message),
              type: 'is-danger'
            });
          })
          .finally(() => {
            this.isExecuting = false;
          });
    }
  },
  data() {
    return {
      salariesList: [],
      fromInput: '',
      toInput: '',
      fromSelected: null,
      toSelected: null,
      mode: 'replace',
      switchDay: null,
      switchMonth: null,
      switchYear: null,
      joursConsommes: null,
      projectFilter: '',
      isLoading: false,
      isExecuting: false,
      result: null
    }
  }
}
</script>

<style scoped>
.migration-replace .message-header {
  background-color: #335280;
  color: white;
}
.migration-replace .message-body {
  border-color: #335280;
  color: #333;
}
.migration-prorata .message-header {
  background-color: #E95420;
  color: white;
}
.migration-prorata .message-body {
  border-color: #E95420;
}
.btn-simulate {
  background-color: #7B2D8E !important;
  color: white !important;
  border: none !important;
  font-weight: 600;
  padding-left: 1.5em;
  padding-right: 1.5em;
}
.btn-simulate:hover {
  background-color: #5E2170 !important;
}
.scroll-hint {
  margin-top: 0.75rem;
  color: #335280;
  cursor: pointer;
  animation: bounce 1.5s ease infinite;
}
.scroll-hint:hover {
  color: #264060;
}
@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(6px); }
}

/* Bouton export */
.btn-export {
  background-color: #335280 !important;
  color: white !important;
  border: none !important;
  font-weight: 600;
}
.btn-export:hover {
  background-color: #264060 !important;
}

/* Tags personnalisés pour les actions */
.tag-prorata {
  background-color: #48c774 !important;
  color: white !important;
}
.tag-integral {
  background-color: #335280 !important;
  color: white !important;
}
.tag-skip {
  background-color: #FFD700 !important;
  color: #333 !important;
}

/* Légende */
.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: #f9f9f9;
  border-radius: 4px;
  border: 1px solid #eee;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
}
.legend-item small {
  color: #666;
}
</style>
